import React, { ComponentType, useContext, useEffect, useRef, useState } from 'react';

import { FormSmsDisclaimer } from '../FormSmsDisclaimer';

import { PrivacyPolicyLink } from './PrivacyPolicyLink';
import * as Styled from './EveryActionPetitionForm.styles';
import { EditYourMessageModalButton } from './EditYourMessageModalButton';
import { DetailsStepContent } from './DetailsStepContent';
import { MessageStepContent } from './MessageStepContent';
import { PreFormContent } from './PreFormContent';

import * as FormStyled from '+/styles/styledComponents/Form.styles';
import {
  getTwoToneTheming,
  FormBaseShades,
  FormBackgroundStyles,
} from '+/styles/styledComponents/Form.styles';
import { ColorTheme } from '+/components/ColorTheme';
import { Markdown } from '+/components/EveryActionPetitionForm/EveryActionPetitionForm.styles';
import { ColorThemeContext, handleDeleteUserData, postUserData } from '+/contexts';
import { useOnScreen } from '+/utils/useOnScreen';
import { useScrollPosition } from '+/utils/useScrollPosition';

const MESSAGE_FIELDS = ['Subject0', 'Message0'];

export const EveryActionPetitionForm = (props: EveryActionPetitionFormProps) => {
  const {
    everyActionPetitionFormData,
    advocacyFields,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    suppliedOptions,
    setFieldValue,
    setFieldTouched,
    validateForm,
    formType,
    formHeaderRef,
  } = props;
  const isUS = values.Country === 'US';
  const displaySmsOptIn = values.MobilePhone && isUS;
  const [rememberMeChecked, updateRememberMeChecked] = useState(isUS);

  const isAdvocacyForm = formType === 'AdvocacyForm';

  // Configure message display for advocacy forms
  const includeMessageStep = isAdvocacyForm && !!everyActionPetitionFormData.includeMessageStep;
  const includeMessageModal = isAdvocacyForm && !includeMessageStep;
  const [onMessageStep, setOnMessageStep] = useState(false);

  // Determine when to display catch-all form error
  const formHasErrors = Object.values(errors).some(errorMessage => errorMessage);
  const [displayFormError, setDisplayFormError] = useState(false);

  useEffect(() => {
    if (!formHasErrors) setDisplayFormError(false);
    return () => setDisplayFormError(false);
  }, [formHasErrors]);

  // Determine whether and when form background color should transition
  const [scrolled, setScrolled] = useState(false);
  const petitionFieldsRef = useRef(null);
  const petitionFieldsOnScreen = useOnScreen(petitionFieldsRef, 0, 0.1, scrolled);

  const parentColorTheme = useContext(ColorThemeContext);
  const { accentedColorTheme, formColorTheme, formBaseShade } = getTwoToneTheming(
    everyActionPetitionFormData.formBackgroundStyle,
    parentColorTheme,
  );
  const [formColorTransitioned, setFormColorTransitioned] = useState(true);

  useScrollPosition(({ currPos }) => {
    if (currPos.y && !scrolled) setScrolled(true);
  });

  useEffect(() => {
    const shouldFormColorTransition =
      petitionFieldsOnScreen &&
      everyActionPetitionFormData.formBackgroundStyle !== FormBackgroundStyles.Default;

    if (shouldFormColorTransition && !formColorTransitioned) setFormColorTransitioned(true);
    else setFormColorTransitioned(false);
  }, [petitionFieldsOnScreen]);

  const handleClickNext = async () => {
    const errs = await validateForm();
    const erroredFields = Object.keys(errs).filter(field => !MESSAGE_FIELDS.includes(field));
    if (erroredFields.length) {
      erroredFields.forEach(field => setFieldTouched(field, true));
      setDisplayFormError(true);
    } else {
      setOnMessageStep(true);
      setDisplayFormError(false);
    }
  };

  const handleClickSubmit = () => {
    handleSubmit();
    setDisplayFormError(formHasErrors);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !displayFormError &&
      rememberMeChecked &&
      postUserData(
        {
          firstName: values.FirstName,
          lastName: values.LastName,
          address1: '',
          postcode: values.PostalCode,
          locality: '',
          state: '',
          country: values.Country,
          email: values.EmailAddress,
          address2: '',
          mobilePhone: values.MobilePhone || '',
        },
        rememberMeChecked,
      );
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !rememberMeChecked && handleDeleteUserData(rememberMeChecked);
  };

  return (
    <Styled.PetitionFormContent
      colorTheme={formColorTransitioned ? accentedColorTheme : formColorTheme}
      baseShade={formColorTransitioned ? FormBaseShades.Dark : formBaseShade}
    >
      <FormStyled.Container>
        {!values.showThankYou && (
          <>
            <div ref={formHeaderRef} id="take-action">
              <Styled.PetitionFormHeader>
                <Markdown>{everyActionPetitionFormData.formHeader}</Markdown>
              </Styled.PetitionFormHeader>
            </div>
            {includeMessageModal && (
              <EditYourMessageModalButton
                values={values}
                handleBlur={handleBlur}
                errors={errors}
                handleChange={handleChange}
                touched={touched}
                intro={advocacyFields.intro}
                outro={advocacyFields.outro}
              />
            )}
            {everyActionPetitionFormData.formText && (
              <FormStyled.Text>
                <Markdown>{everyActionPetitionFormData.formText}</Markdown>
              </FormStyled.Text>
            )}
            <Styled.PetitionFields ref={petitionFieldsRef}>
              <FormStyled.Body className="pet-form__body">
                <FormStyled.Section className="pet-form__section">
                  <Styled.StepsContainer>
                    <DetailsStepContent
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      isSubmitting={isSubmitting}
                      suppliedOptions={suppliedOptions}
                      includeMessageStep={includeMessageStep}
                      isAdvocacyForm={isAdvocacyForm}
                      displayFormError={displayFormError}
                      setDisplayFormError={setDisplayFormError}
                      onMessageStep={onMessageStep}
                      setOnMessageStep={setOnMessageStep}
                      handleSubmit={handleClickSubmit}
                      handleClickNext={handleClickNext}
                      rememberMeChecked={rememberMeChecked}
                      updateRememberMeChecked={updateRememberMeChecked}
                      isUS={isUS}
                      displaySmsOptIn={displaySmsOptIn}
                    />
                    {includeMessageStep && (
                      <MessageStepContent
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        advocacyFields={advocacyFields}
                        isSubmitting={isSubmitting}
                        displayFormError={displayFormError}
                        onMessageStep={onMessageStep}
                        setOnMessageStep={setOnMessageStep}
                        setDisplayFormError={setDisplayFormError}
                        handleSubmit={handleClickSubmit}
                      />
                    )}
                  </Styled.StepsContainer>
                </FormStyled.Section>
              </FormStyled.Body>
              <Styled.Disclaimers>
                <PrivacyPolicyLink />
                {displaySmsOptIn && <FormSmsDisclaimer />}
              </Styled.Disclaimers>
              <br />
            </Styled.PetitionFields>
          </>
        )}
        {values.showThankYou && (
          <>
            <Styled.PetitionFormHeader>
              <Markdown>
                {everyActionPetitionFormData.thankYouHeader
                  ? everyActionPetitionFormData.thankYouHeader
                  : '## Thank you for taking action!'}
              </Markdown>
            </Styled.PetitionFormHeader>
            <FormStyled.Text>
              <Markdown>
                {everyActionPetitionFormData.thankYouText
                  ? everyActionPetitionFormData.thankYouText
                  : 'Every day, living, feeling animals are born into a cycle of pain and suffering. But we are changing that.'}
              </Markdown>
            </FormStyled.Text>
          </>
        )}
      </FormStyled.Container>
    </Styled.PetitionFormContent>
  );
};

export const FormLayout = ({
  formType,
  advocacyFields,
  everyActionPetitionFormData,
  suppliedOptions,
  FormContent,
}: {
  formType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  advocacyFields: any;
  everyActionPetitionFormData: EveryActionPetitionFormComponent;
  suppliedOptions: { table_countries?: CountryList };
  FormContent: ComponentType<FormikPetitionFormProps>;
}) => {
  // Get form pre-content background color
  const parentColorTheme = useContext(ColorThemeContext);
  const colorTheme = everyActionPetitionFormData.colorTheme || parentColorTheme;
  const { accentedColorTheme, formColorTheme } = getTwoToneTheming(
    everyActionPetitionFormData.formBackgroundStyle,
    colorTheme,
  );

  const formHeaderRef = useRef<HTMLDivElement>(null);
  const formContentRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <ColorTheme colorTheme={accentedColorTheme}>
        <PreFormContent
          formId={everyActionPetitionFormData.formId}
          progressBarStatus={everyActionPetitionFormData.progressBarStatus}
          textAboveProgressBar={everyActionPetitionFormData.textAboveProgressBar}
          textBelowProgressBar={everyActionPetitionFormData.textBelowProgressBar}
          progressBarTargetLabel={everyActionPetitionFormData.progressBarTargetLabel}
          progressBarValueLabel={everyActionPetitionFormData.progressBarValueLabel}
          formContentRef={formContentRef}
          formHeaderRef={formHeaderRef}
        />
      </ColorTheme>
      <ColorTheme colorTheme={formColorTheme}>
        <div ref={formContentRef}>
          <FormContent
            formType={formType}
            advocacyFields={advocacyFields}
            everyActionPetitionFormData={everyActionPetitionFormData}
            suppliedOptions={suppliedOptions}
            formHeaderRef={formHeaderRef}
          />
        </div>
      </ColorTheme>
    </>
  );
};
